import React, { useState, useEffect } from 'react';
import { X, PlayCircle, Compass, Rocket, CheckCircle } from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../firebase/firebase';

const LandingWalkthrough = () => {
  const [showInvite, setShowInvite] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Unique key for the landing page tour memory
  const STORAGE_KEY = 'hasSeenLandingPageTour_v2';

  useEffect(() => {
    // 1. Check if first time visitor (or hasn't seen this specific tour version)
    const hasSeenInvite = localStorage.getItem(STORAGE_KEY);

    if (!hasSeenInvite) {
      // 2. Wait 10 seconds before showing the centered popup
      const timer = setTimeout(() => {
        setShowInvite(true);
        // Log that the invite appeared
        if (analytics) {
          logEvent(analytics, 'landing_tour_invite_shown');
        }
      }, 10000);

      return () => clearTimeout(timer);
    }
  }, []);

  const handleStartTour = () => {
    if (analytics) {
      logEvent(analytics, 'landing_tour_started');
    }
    setShowInvite(false);
    setIsTourOpen(true);
    // Mark as seen so it doesn't annoy them on reload
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleCloseInvite = () => {
    setShowInvite(false);
    // Mark as seen so we don't show it again
    localStorage.setItem(STORAGE_KEY, 'true');
    if (analytics) {
      logEvent(analytics, 'landing_tour_invite_dismissed');
    }
  };

  const handleCloseTour = () => {
    setIsTourOpen(false);
  };

  // If neither is open, render nothing (invisible)
  if (!showInvite && !isTourOpen) return null;

  return (
    <>
      {/* --- 1. CENTERED INVITATION POPUP (Landing-Page Style) --- */}
      {showInvite && !isTourOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 animate-in fade-in duration-500">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
            onClick={handleCloseInvite}
          />

          {/* Refined Card */}
          <div className="relative max-w-lg w-full bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden transform transition-all scale-100">
            {/* Top Accent Bar */}
            <div className="h-1 w-full bg-gradient-to-r from-sky-500 via-blue-500 to-emerald-500" />

            <button
              onClick={handleCloseInvite}
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-full hover:bg-slate-100"
              aria-label="Close"
            >
              <X size={20} />
            </button>

            <div className="p-7 sm:p-8">
              {/* Badge + Icon */}
              <div className="flex items-center justify-center gap-3 mb-5">
                <div className="inline-flex items-center gap-2 rounded-full bg-sky-50 px-3 py-1 border border-sky-100">
                  <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-sky-600">
                    <Rocket className="h-3.5 w-3.5 text-white" />
                  </span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-sky-700">
                    Guided Product Tour
                  </span>
                </div>
              </div>

              {/* Typography */}
              <h3 className="text-2xl sm:text-3xl font-extrabold text-sky-900 text-center tracking-tight mb-3">
                See the TPT Seller App in Action
              </h3>

              <p className="text-slate-600 text-[15px] leading-relaxed mb-5 text-center max-w-md mx-auto">
                Take a quick, interactive walkthrough to see how sellers import their store, fix SEO,
                and ship better listings in just a few clicks.
              </p>

              {/* Benefits (aligned with landing page tone) */}
              <ul className="mt-3 mb-6 text-sm text-slate-700 space-y-2 text-left max-w-md mx-auto">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>Watch how to import your entire TPT store into one dashboard.</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>See live SEO rank tracking and quick-win improvement suggestions.</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                  <span>Preview the tools before starting your free trial—no card required.</span>
                </li>
              </ul>

              {/* Buttons */}
              <div className="space-y-3">
                <button
                  onClick={handleStartTour}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white text-base font-semibold py-3.5 px-6 rounded-full shadow-md hover:shadow-lg hover:scale-[1.02] transition-all flex items-center justify-center group"
                >
                  <PlayCircle className="w-5 h-5 mr-2 group-hover:scale-105 transition-transform" />
                  Start the 2-Minute Interactive Tour
                </button>

                <button
                  onClick={handleCloseInvite}
                  className="w-full py-2 text-xs font-medium text-slate-500 hover:text-slate-700 transition-colors"
                >
                  I’ll explore the site on my own
                </button>

                <p className="text-[11px] text-slate-400 text-center">
                  You can always open the tour again from the walkthrough section on the page.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- 2. FULL TOUR MODAL (Same Style as App) --- */}
      {isTourOpen && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-300"
          onClick={handleCloseTour}
        >
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800">
              <h3 className="text-lg font-semibold text-sky-400 flex items-center">
                <Compass className="h-5 w-5 mr-2" />
                Platform Walkthrough
              </h3>
              <button
                onClick={handleCloseTour}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Embed */}
            <div className="flex-1 relative overflow-y-auto flex items-center justify-center bg-slate-900">
              <div
                style={{
                  position: 'relative',
                  boxSizing: 'content-box',
                  maxHeight: '85vh',
                  width: '100%',
                  aspectRatio: '2.0146904512067154',
                  padding: '40px 0 40px 0',
                  background: '#0f172a',
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cmibzew5g8mfpb7b4l3tkxyq8?embed_v=2&utm_source=embed"
                  loading="lazy"
                  title="Mr Joel's Seller Tools: The TPT Seller App Overview"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default LandingWalkthrough;
